/* eslint-disable react/no-unsafe */
import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { propertyOperators } from "@appcues/libcues";
import { connect } from "react-redux";
import { Button } from "@appcues/component-library";
import moment from "moment";
import AudienceBaseClause from "components/Common/Clauses/AudienceBase";
import SelectProfileAttribute from "components/Common/SelectProfileAttribute";
import AutosuggestProfileAttributes from "components/Common/AutosuggestProfileAttribute";
import ToggleableSelect from "components/Common/Clauses/ToggleableSelect";
import ToggleableInput from "components/Common/Clauses/ToggleableInput";
import ReadOnlyField from "components/Common/ReadOnlyField";
import { millisecondsPerDay } from "constants/account/conditions";
import { isTimestamp } from "utils";
import { prepareClauseUpdates } from "utils/conditions";
import { selectFilteredProfileAttributes } from "reducers/account/profileAttributes";

import PropertyTextArea from "./PropertyTextArea";
import "react-datepicker/dist/react-datepicker.css";

export class PropertyClause extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showAdditionalInfo: undefined,
      showError: false,
      propertyLength: 0
    };
  }

  componentDidMount() {
    const { clause } = this.props;

    this.setState({
      propertyLength: (`${clause.value}` || "").split("\n").length
    });
  }

  componentWillReceiveProps(nextProps) {
    const { clause } = nextProps;

    if (this.state.showAdditionalInfo === undefined) {
      this.setState({
        showAdditionalInfo:
          clause.property &&
          clause.operator &&
          [propertyOperators.IN, propertyOperators.NOT_IN].includes(
            clause.operator
          ) &&
          (`${clause.value}` || "").split("\n").length < 2
      });
    }
  }

  handleDismissError = () => {
    this.setState({ showError: false });
  };

  handleChange = e => {
    const { clause, updateClause } = this.props;

    const value = e.target.value;
    const formatted = value.replace(/\\,/g, "&#44;").split(/[,\n]+/);

    // we want to base length on number of comma separated values and not the string values actual length
    if (formatted.length > 1000) {
      const limited = formatted.slice(0, 1000);

      this.setState({
        showError: true,
        propertyLength: limited.length
      });

      updateClause(clause.id, {
        value: limited.join("\n")
      });
    } else {
      this.setState({
        propertyLength: formatted.length,
        showError: false
      });

      updateClause(clause.id, {
        value: formatted.join("\n")
      });
    }
  };

  render() {
    const { clause, profileAttributes, readOnly, updateClause } = this.props;

    const propertyMeta = Object.values(profileAttributes || {}).find(
      attr => attr.name === clause.property
    );

    const firstSuggestion =
      propertyMeta && propertyMeta.values ? propertyMeta.values[0] : "";
    const isPropertyTimestamp =
      Object.values(profileAttributes || {}).find(
        it => it.name === clause.property
      ) && isTimestamp(firstSuggestion);

    // properties without open-ended inputs

    const presetProperties = {
      _deviceType: ["desktop", "mobile"],
      _operatingSystem: ["Mac", "Windows", "iOS", "Android", "other"],
      _browser: [
        "Microsoft Edge",
        "Internet Explorer",
        "Chrome",
        "Firefox",
        "UC Browser",
        "Android",
        "Safari",
        "BlackBerry",
        "Apple CoreMedia",
        "iPad",
        "iPhone",
        "iPod Touch",
        "Nintendo",
        "Opera",
        "PhantomJS",
        "PlayStation Portable",
        "PlayStation",
        "QuickTime",
        "Safari",
        "Xbox",
        "other"
      ]
    };

    const isPresetProperty = Object.keys(presetProperties).includes(
      clause.property
    );

    // build a list of all the valid operators given the property

    let operatorOptions = [];

    if (isPresetProperty) {
      operatorOptions = [
        { value: propertyOperators.EQUALS, label: "is" },
        { value: propertyOperators.NOT_EQUALS, label: "isn't" }
      ];
    } else {
      operatorOptions = [
        ...operatorOptions,
        { value: propertyOperators.EQUALS, label: "equals" },
        { value: propertyOperators.NOT_EQUALS, label: "doesn't equal" },
        { value: propertyOperators.CONTAINS, label: "contains" },
        { value: propertyOperators.NOT_CONTAINS, label: "doesn't contain" },
        { value: propertyOperators.STARTS_WITH, label: "starts with" },
        {
          value: propertyOperators.NOT_STARTS_WITH,
          label: "doesn't start with"
        },
        { value: propertyOperators.ENDS_WITH, label: "ends with" },
        { value: propertyOperators.NOT_ENDS_WITH, label: "doesn't end with" },
        { value: propertyOperators.EXISTS, label: "exists" },
        { value: propertyOperators.NOT_EXISTS, label: "doesn't exist" },
        { value: propertyOperators.IN, label: "is one of" },
        { value: propertyOperators.NOT_IN, label: "isn't one of" },
        { value: propertyOperators.GREATER_THAN, label: "is greater than" },
        { value: propertyOperators.LESS_THAN, label: "is less than" },
        { value: propertyOperators.REGEX, label: "matches regex" }
      ];

      if (isPropertyTimestamp) {
        operatorOptions = [
          {
            value: propertyOperators.GREATER_THAN_X_DAYS_AGO,
            label: "occurred more than"
          },
          {
            value: propertyOperators.LESS_THAN_X_DAYS_AGO,
            label: "occurred less than"
          },
          {
            value: propertyOperators.WITHIN_X_DAYS_AGO,
            label: "will occur within the next"
          },
          {
            value: propertyOperators.GREATER_THAN_TIME,
            label: "occurred after"
          },
          { value: propertyOperators.LESS_THAN_TIME, label: "occurred before" },
          ...operatorOptions
        ];
      }
    }

    const onValueChange = newValue => {
      updateClause(clause.id, { value: newValue });
    };

    const onRelativeTimeValueChange = e => {
      const numDays = parseInt(e.target.value, 10);
      if (e.target.value === "" || numDays) {
        updateClause(clause.id, {
          value: numDays ? numDays * millisecondsPerDay : ""
        });
      }
    };

    const selectedOption = operatorOptions.find(
      operator => operator.value === clause.operator
    );

    const selectedOptionDisplay = selectedOption ? selectedOption.label : "";

    const datePickerDisplayTime = moment(parseInt(clause.value, 10)).format(
      "L"
    );

    const parsedTime = parseInt(clause.value, 10) / millisecondsPerDay;
    const timeValue = clause.value ? parsedTime : "";

    return (
      <PropertyContainer>
        <AudienceBaseClause {...this.props}>
          <div className="property-targeting sub-clause">
            <SelectProfileAttribute
              value={clause.property}
              onChange={opt => {
                if (Object.keys(presetProperties).includes(opt.value)) {
                  updateClause(clause.id, {
                    property: opt.value,
                    value: presetProperties[opt.value][0]
                  });
                } else if (isPresetProperty) {
                  updateClause(clause.id, { property: opt.value, value: "" });
                } else {
                  updateClause(clause.id, { property: opt.value });
                }
              }}
              readOnly={readOnly}
            />

            {(() => {
              return (
                <ToggleableSelect
                  className="select-operator"
                  clearable={false}
                  readOnlyContent={readOnly && selectedOptionDisplay}
                  value={clause.operator}
                  options={operatorOptions}
                  autosize={false}
                  onChange={opt => {
                    updateClause(
                      clause.id,
                      prepareClauseUpdates(clause, opt.value)
                    );

                    this.setState({
                      showAdditionalInfo: [
                        propertyOperators.IN,
                        propertyOperators.NOT_IN
                      ].includes(opt.value)
                    });
                  }}
                  operator
                  readOnly={readOnly}
                />
              );
            })()}

            {(() => {
              switch (clause.operator) {
                case propertyOperators.EXISTS:
                case propertyOperators.NOT_EXISTS:
                  break;
                case propertyOperators.GREATER_THAN_X_DAYS_AGO:
                case propertyOperators.LESS_THAN_X_DAYS_AGO:
                  return (
                    <label>
                      <ToggleableInput
                        type="text"
                        value={timeValue}
                        onChange={onRelativeTimeValueChange}
                        readOnly={readOnly}
                        readOnlyContent={timeValue}
                      />{" "}
                      {parsedTime ? "day" : "days"} ago
                    </label>
                  );
                case propertyOperators.WITHIN_X_DAYS_AGO:
                  return (
                    <label>
                      <ToggleableInput
                        onChange={onRelativeTimeValueChange}
                        readOnly={readOnly}
                        readOnlyContent={timeValue}
                        type="text"
                        value={timeValue}
                      />{" "}
                      {parsedTime ? "day" : "days"}
                    </label>
                  );
                case propertyOperators.IN:
                case propertyOperators.NOT_IN:
                  return (
                    <Button
                      className="timeframe-options"
                      onClick={() => {
                        this.setState(({ showAdditionalInfo }) => ({
                          showAdditionalInfo: !showAdditionalInfo
                        }));
                      }}
                    >
                      {(() => {
                        const numValues = (`${clause.value}` || "").split("\n")
                          .length;
                        const units = numValues > 1 ? "values" : "value";

                        if (this.state.showAdditionalInfo) {
                          return `Hide ${numValues} ${units}`;
                        }
                        return `Show ${numValues} ${units}`;
                      })()}
                    </Button>
                  );
                case propertyOperators.LESS_THAN_TIME:
                case propertyOperators.GREATER_THAN_TIME:
                  return (
                    <div>
                      <ToggleableInput
                        datePicker
                        selected={moment(parseInt(clause.value, 10))}
                        onChange={date =>
                          updateClause(clause.id, {
                            value: date.startOf("day").format("x")
                          })
                        }
                        readOnly={readOnly}
                        readOnlyContent={datePickerDisplayTime}
                      />
                    </div>
                  );
                default:
                  if (isPropertyTimestamp) {
                    const hasValue =
                      clause.value !== undefined ? clause.value : "";

                    return (
                      <div>
                        <ToggleableInput
                          readOnly={readOnly}
                          readOnlyContent={hasValue}
                          type="text"
                          value={hasValue}
                          onChange={e => onValueChange(e.target.value)}
                        />
                      </div>
                    );
                  } else if (isPresetProperty) {
                    return (
                      <ToggleableSelect
                        readOnlyContent={clause.value}
                        className="select-value"
                        clearable={false}
                        value={clause.value}
                        options={presetProperties[clause.property].map(
                          value => {
                            return {
                              label: value,
                              value
                            };
                          }
                        )}
                        autosize={false}
                        onChange={opt => {
                          updateClause(clause.id, { value: opt.value });
                        }}
                        readOnly={readOnly}
                      />
                    );
                  }
                  return (
                    <PropertyValueContainer>
                      {readOnly ? (
                        <ReadOnlyField
                          content={
                            clause.value !== undefined ? clause.value : ""
                          }
                        />
                      ) : (
                        <StyledAutosuggest
                          value={clause.value !== undefined ? clause.value : ""}
                          property={clause.property}
                          onChange={onValueChange}
                        />
                      )}
                    </PropertyValueContainer>
                  );
              }
              return null;
            })()}
          </div>
        </AudienceBaseClause>
        {(() => {
          if (this.state.showAdditionalInfo) {
            /**
             * Note that though values are stored in Firebase as `\n` delimited, we allow users to
             * paste text separated by either `\n` or comma.  In the rare case that somebody needs
             * to specify a comma in one of the values, we allow them to escape it as `\,`.
             *
             * For the reasons above, we convert all `\,` to character codes (&#44;) before
             * saving, and convert them back before showing to the user.
             */

            return (
              <AdditionalInfo>
                {readOnly ? (
                  <ReadOnlyField
                    content={(`${clause.value}` || "").replace(/&#44;/g, "\\,")}
                  />
                ) : (
                  <PropertyTextArea
                    isErrored={this.state.showError}
                    value={clause.value}
                    propertyLength={this.state.propertyLength}
                    onDismissError={this.handleDismissError}
                    onChange={this.handleChange}
                  />
                )}
              </AdditionalInfo>
            );
          }
          return null;
        })()}
      </PropertyContainer>
    );
  }
}

// language=SCSS prefix=dummy{ suffix=}
const PropertyContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  .select-operator {
    display: flex;
    flex-grow: 1;
  }
  .select-value,
  input {
    width: 100%;
  }
  label {
    display: flex;
    flex-grow: 1;
    align-items: center;
    white-space: nowrap;
    input {
      margin-right: 5px;
    }
  }
  .button {
    margin: 0;
  }
  .react-datepicker__input-container {
    display: flex;
    width: 100%;
  }
`;

// language=SCSS prefix=dummy{ suffix=}
const AdditionalInfo = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin-top: 0.5em;
  textarea {
    width: 100%;
  }
`;

const PropertyValueContainer = styled.div`
  flex-grow: 1;
`;

const StyledAutosuggest = styled(AutosuggestProfileAttributes)`
  min-width: 80px;
`;
// For testing the field in Jest
StyledAutosuggest.displayName = "StyledAutosuggest";

PropertyClause.propTypes = {
  clause: PropTypes.object,
  profileAttributes: PropTypes.object,
  readOnly: PropTypes.bool,
  updateClause: PropTypes.func
};

function mapStateToProps(state) {
  return {
    profileAttributes: selectFilteredProfileAttributes(state)
  };
}

export default connect(mapStateToProps)(PropertyClause);
